// Shared wiring for the three personalised-product templates.
// Shopify's rendered variant is authoritative; never trust an arbitrary URL id.
(() => {
  const controllers = new WeakMap();

  function initialize(form) {
    if (controllers.has(form)) return;
    const section = form.closest('.shopify-section');
    const productId = form.closest('[data-product-id]')?.dataset.productId;
    const idInput = form.querySelector('.pp-variant-id');
    const quantity = form.querySelector('.pp-quantity-input');
    const button = form.querySelector('.pp-add-to-cart');
    let variants;
    try { variants = JSON.parse(form.querySelector('[data-pp-product-variants]').textContent); }
    catch { return; }
    if (!section || !productId || !idInput || !Array.isArray(variants)) return;
    const byId = new Map(variants.map(variant => [String(variant.id), variant]));
    if (!byId.has(idInput.value)) return;

    const life = new AbortController();
    const { signal } = life;
    controllers.set(form, life);
    let pending = false;

    function normalizeQuantity() {
      if (!quantity) return 1;
      const value = parseInt(quantity.value, 10);
      if (!value || value < 1) quantity.value = '1';
      return Math.max(1, value || 1);
    }

    function syncVariant(variant) {
      if (variant) idInput.value = String(variant.id);
      if (!button) return;
      const available = Boolean(variant?.available);
      button.dataset.ppVariantAvailable = String(available);
      button.disabled = pending || !available || button.getAttribute('aria-busy') === 'true';
      const label = button.querySelector('span:last-child');
      if (label) label.textContent = available ? 'In den Warenkorb legen' : 'Derzeit nicht verfügbar';
    }

    function fromThisProduct(event) {
      return event.target.closest?.('variant-picker')?.dataset.productId === productId;
    }

    for (const [selector, step] of [['.pp-qty-minus', -1], ['.pp-qty-plus', 1]]) {
      form.querySelector(selector)?.addEventListener('click', () => {
        if (!quantity) return;
        quantity.value = String(Math.max(1, normalizeQuantity() + step));
        quantity.dispatchEvent(new Event('change', { bubbles: true }));
      }, { signal });
    }
    quantity?.addEventListener('change', normalizeQuantity, { signal });

    // Native picker events stay confined to this section AND product. This also
    // keeps the standard picker safe if Gallery's six-colour enhancement falls back.
    section.addEventListener('variant:selected', event => {
      if (!fromThisProduct(event)) return;
      pending = true;
      if (button) button.disabled = true;
    }, { signal });
    section.addEventListener('variant:update', event => {
      if (!fromThisProduct(event) || String(event.detail?.data?.productId) !== productId) return;
      pending = false;
      const variant = event.detail?.resource;
      if (variant && byId.has(String(variant.id))) {
        byId.set(String(variant.id), variant);
        syncVariant(variant);
      } else {
        idInput.value = '';
        syncVariant(null);
      }
    }, { signal });
    form.addEventListener('change', event => {
      const input = event.target;
      if (input.name !== 'id' || input === idInput || input.form !== form) return;
      const variant = byId.get(input.value);
      if (variant) syncVariant(variant);
    }, { signal });

    // Capture runs before the optional Priority service. Upload/consent checks
    // remain in their existing blocks, and no media or preview event is emitted.
    form.addEventListener('submit', event => {
      normalizeQuantity();
      const variant = byId.get(idInput.value);
      syncVariant(variant);
      if (pending || !variant?.available) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, { capture: true, signal });
    window.addEventListener('pageshow', () => syncVariant(byId.get(idInput.value)), { signal });
    form.setAttribute('enctype', 'multipart/form-data');
    syncVariant(byId.get(idInput.value));
  }

  function initializeAll(root = document) {
    root.querySelectorAll('form.pp-product-form').forEach(initialize);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(), { once: true });
  } else {
    initializeAll();
  }
  document.addEventListener('shopify:section:load', event => initializeAll(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('form.pp-product-form').forEach(form => {
      controllers.get(form)?.abort();
      controllers.delete(form);
    });
  });
})();
